import { ChangeDetectorRef, Component } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { first, takeUntil, tap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import {
  ActivitySubscriptionsCompanyComponent,
  ActivitySubscriptionsFollowedByMeOfferComponent,
  ActivitySubscriptionsFollowOfferComponent,
} from '@recruitee/activities-and-notifications';
import { FeatureGuardFacade, GuardedFeature } from '@recruitee/feature-guard';
import {
  Ability,
  ActivitySubscriptionType,
  ActivitySubscriptionUpdateData,
} from '@recruitee/http-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  SlackIntegration,
  SlackIntegrationActions,
  SlackIntegrationSelectors,
} from '@recruitee/slack-integration-data';
import {
  ButtonComponent,
  ConfirmDialogVariant,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  DividerComponent,
  IconComponent,
  RtStatefulComponent,
  TabComponent,
  TabsComponent,
} from '@recruitee/user-interface';

type State = {
  slackIntegrations: SlackIntegration[];
  slackIntegration: SlackIntegration | null;
  slackIntegrationsLoading: boolean;
  deleting: boolean;
};

@Component({
  selector: 'rt-slack-integration-dialog',
  templateUrl: './slack-integration-dialog.component.html',
  styleUrls: ['./slack-integration-dialog.component.less'],
  standalone: true,
  imports: [
    DialogHeaderComponent,
    TranslatePipe,
    TabComponent,
    TabsComponent,
    DialogBodyComponent,
    ActivitySubscriptionsFollowOfferComponent,
    DividerComponent,
    ActivitySubscriptionsFollowedByMeOfferComponent,
    ActivitySubscriptionsCompanyComponent,
    ButtonComponent,
    IconComponent,
    DialogFooterComponent,
  ],
})
export class SlackIntegrationDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<void, boolean>
{
  public abilities = {
    viewOffers: false,
    viewMailbox: false,
    manageCandidates: false,
    viewCandidates: false,
    manageJobsReferralSettings: false,
    viewAllOffers: false,
    manageRecruiters: false,
    shareCandidates: false,
    manageReferrals: false,
    manageAttributes: false,
    manageDisqualifyReasons: false,
    manageTexting: false,
    viewRequisitions: false,
    manageLocations: false,
  };

  public hasRequisitions = false;

  public readonly ActivitySubscriptionType = ActivitySubscriptionType;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private dialogService: DialogService,
    private featureGuard: FeatureGuardFacade,
    private i18n: I18nService,
    private store: Store,
    public dialogRef: DialogRef<SlackIntegrationDialogComponent>,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.createState({
      slackIntegrations: [],
      slackIntegration: null,
      slackIntegrationsLoading: false,
      deleting: false,
    });
  }

  public ngOnInit(): void {
    this.checkAbilities();
    this.checkRequisitionsFeature();
    this.loadSlackIntegrations();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public loadSlackIntegrations(): void {
    this.store
      .select(SlackIntegrationSelectors.allEntities)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(slackIntegrations => {
        const currentIntegration = this.state.slackIntegration
          ? slackIntegrations!.find(
              integration => integration.id === this.state.slackIntegration!.id,
            )
          : null;

        let slackIntegration: SlackIntegration | null;
        if (currentIntegration) {
          slackIntegration = currentIntegration;
        } else if (slackIntegrations!.length > 0) {
          slackIntegration = slackIntegrations![0];
        } else {
          slackIntegration = null;
        }

        this.setState({
          ...this.state,
          slackIntegrations: slackIntegrations!,
          slackIntegration,
        });
      });

    this.store
      .select(SlackIntegrationSelectors.isPending({ scope: 'main' }))
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(isPending => {
        this.setState({
          ...this.state,
          slackIntegrationsLoading: isPending,
        });
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public setDialogData(): void {}

  public addChannel(): void {
    this.dialogRef.close(true);
  }

  public selectSlackIntegration(slackIntegration: SlackIntegration): void {
    this.setState({
      ...this.state,
      slackIntegration,
    });
  }

  public changeEvents({ events, value }: ActivitySubscriptionUpdateData): void {
    const slackIntegrationId = this.slackIntegration.id;
    const eventsMap: any = new Map();
    events.map(event => (eventsMap[event] = { slack: value }));

    this.store.dispatch(
      SlackIntegrationActions.updateOnePessimistic({
        payload: {
          data: {
            id: slackIntegrationId,
            activitySubscriptions: {
              ...eventsMap,
            },
            channelName: this.slackIntegration.channelName,
          },
        },
      }),
    );
  }

  public get slackIntegration(): SlackIntegration {
    const slackIntegration = this.state.slackIntegration!;
    if (slackIntegration.activitySubscriptions) {
      delete slackIntegration.activitySubscriptions.my;
    }
    return slackIntegration;
  }

  public trackByIntegration(_index: number, integration: SlackIntegration): string {
    return integration.channelName;
  }

  public get actionsDisabled(): boolean {
    return this.state.deleting || this.state.slackIntegrationsLoading;
  }

  // eslint-disable-next-line max-lines-per-function
  public deleteSlackIntegration(slackIntegration: SlackIntegration): void {
    if (this.state.deleting) return;

    this.dialogService
      .confirm({
        variant: ConfirmDialogVariant.Error,
        title: this.i18n.translate('app.settings.integrations.slack.remove_dialog.title'),
        message: this.i18n.translate('app.settings.integrations.slack.remove_dialog.message', {
          channel_name: slackIntegration.channelName,
        }),
        agreeLabel: this.i18n.translate('app.common.remove'),
        cancelLabel: this.i18n.translate('app.common.cancel'),
      })
      .pipe(
        tap(confirmed => {
          if (confirmed) {
            this.setState({ ...this.state, deleting: true });
            this.store.dispatch(
              SlackIntegrationActions.removeOnePessimistic({
                payload: {
                  data: slackIntegration.id,
                },
              }),
            );
          }
        }),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(
        () => {
          if (this.state.slackIntegrations.length === 1) {
            this.dialogRef.close();
          } else {
            this.setState({ ...this.state, deleting: false });
          }
        },
        () => {
          this.setState({ ...this.state, deleting: false });
        },
      );
  }

  private checkRequisitionsFeature(): void {
    this.featureGuard
      .checkFeatureAvailability(GuardedFeature.Requisitions)
      .pipe(first())
      .subscribe(areRequisitionsAvailable => {
        this.hasRequisitions = areRequisitionsAvailable;
        this.cdRef.detectChanges();
      });
  }

  private checkAbilities(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.abilities = {
          viewOffers: enabledMap[Ability.ViewJobs],
          viewMailbox: enabledMap[Ability.ViewMailbox],
          manageCandidates: enabledMap[Ability.ManageCandidates],
          viewCandidates: enabledMap[Ability.ViewCandidates],
          manageJobsReferralSettings: enabledMap[Ability.ManageJobsReferralSettings],
          viewAllOffers: enabledMap[Ability.ViewAllJobs],
          manageRecruiters: enabledMap[Ability.ManageRecruiters],
          shareCandidates: enabledMap[Ability.ShareCandidates],
          manageReferrals: enabledMap[Ability.ManageReferrals],
          manageAttributes: enabledMap[Ability.ManageAttributes],
          manageDisqualifyReasons: enabledMap[Ability.ManageDisqualifyReasons],
          manageTexting: enabledMap[Ability.ManageTexting],
          viewRequisitions: enabledMap[Ability.ViewRequisitions],
          manageLocations: enabledMap[Ability.ManageLocations],
        };

        this.cdRef.detectChanges();
      });
  }
}
